import React from 'react';
import { connect } from 'react-redux';
import Chart from 'react-apexcharts';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Typography,
  Icon,
  Tooltip,
} from '@mui/material';
import { withStyles } from '@mui/styles';

import { TitleTooltip } from './TitleTooltip';
import { LoadingView } from './LoadingView';
import { ServiceLineSelect } from './ServiceLineSelect';
import { PatientTypeSelect } from './PatientTypeSelect';
import { SmallIconTextButton } from './SmallIconTextButton';
import { PDFExportDialog } from './PDFExportDialog';

import {
  StoreUtil,
  PatientTypes,
  PatientType,
  SpecialtyUtil,
} from 'doctivity-shared/utils';
import {
  ColorUtil,
  ServiceLineUtil,
  StringUtil,
  withIsMobile,
  withRouter
} from 'utils';
import { loadAnalytics } from 'store/actions/analyticsActions';
import { listClaimsServicelines } from 'store/actions/claimsActions';
import fileDownload from 'js-file-download';
import { CsvUtil, DateUtil } from 'doctivity-shared/utils';

// Distinct redux slot for this card's copy of CLAIMS_NETWORK_SPECIALTIES so it
// does not collide with ClaimsNetwork's, which is loaded on the same page.
const SPECIALTIES_STORE_ID = 'CLAIMS_NETWORK_AFFILIATED_SPECIALTIES';

const styles = (theme) => ({
  card: {},
  filterDropDown: {
    width: 320,
    marginRight: theme.spacing(),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  specialtyDropDown: {
    width: 180,
    marginRight: theme.spacing(),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  dropDown: {
    marginBottom: theme.spacing(2),
  },
  select: {
    '& .MuiMenuItem-root.Mui-selected, .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: '#00a887',
      color: 'white',
    },
  },
  dropDownContainer: {
    display: 'flex',
    flexDirection: 'row',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  filterLabel: {
    backgroundColor: 'white',
    paddingRight: 4,
  },
  noData: {
    textAlign: 'center',
    paddingTop: 50,
    minHeight: 200,
  },
  colorKey: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 400,
    minHeight: 40,
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  colorKeyIcon: {
    width: 14,
    height: 14,
    marginRight: 2,
    borderRadius: 2,
  },
  colorKeyText: {
    marginRight: 12,
  },
  affiliatedContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  affiliatedHeader: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
  },
  affiliated: {
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 2,
    backgroundColor: ColorUtil.AffiliatedColor,
    color: '#FFFFFF',
    padding: '4px 12px',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      fontSize: 11,
      padding: '3px 8px',
    },
  },
  nonAffiliated: {
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 2,
    backgroundColor: ColorUtil.NonAffiliatedColor,
    color: '#FFFFFF',
    padding: '4px 12px',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      fontSize: 11,
      padding: '3px 8px',
    },
  },
  affiliatedSpacer: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(1),
    },
  },
  affiliatedPercentage: {
    fontSize: 14,
    fontWeight: 600,
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.down('md')]: {
      fontSize: 12,
    },
  },
  affiliatedIcon: {
    fontSize: 14,
    fontWeight: 400,
    marginRight: theme.spacing(1),
    color: '#FFFFFF',
    [theme.breakpoints.down('md')]: {
      fontSize: 12,
      marginRight: theme.spacing(0.5),
    },
  },
  affiliatedInfoTipIcon: {
    position: 'relative',
    top: -4,
    marginLeft: theme.spacing(),
    fontSize: 14,
    fontWeight: 200,
    color: '#999999',
  },
  affiliatedInfoTip: {
    fontSize: 13,
    fontWeight: 200,
  },
});

class ClaimsNetworkAffiliated extends React.Component {
  constructor(props) {
    super(props);

    this.cardElement = React.createRef(null);

    this.state = {
      serviceline: 'all',
      before: false,
      // Selected taxonomy codes. Empty === "All" (no taxonomy_code sent).
      specialty: [],
      showPdfExport: false,
    };
  }

  componentDidMount() {
    const { network } = this.props;

    if (StoreUtil.needsLoadNoCache(network)) {
      this.fetchData();
    }
    this.fetchSpecialtiesData();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.provider !== prevProps.provider ||
      this.props.patientType !== prevProps.patientType
    ) {
      // The available specialties depend on the provider's shared network, so
      // a previously selected code may not exist in the new list. Clear back to
      // "All" rather than silently filtering everything out. Guarded so an
      // already-empty selection doesn't force an extra render pass.
      if (this.state.specialty.length > 0) {
        this.setState({ specialty: [] }, () => {
          this.fetchData();
          this.fetchSpecialtiesData();
        });
      } else {
        this.fetchData();
        this.fetchSpecialtiesData();
      }
    }
  }

  fetchData() {
    const { dispatch, provider, patientType, clientId, servicelines } =
      this.props;

    if (!servicelines) {
      dispatch(listClaimsServicelines());
    }

    dispatch(
      loadAnalytics({
        type: 'CLAIMS_NETWORK_AFFILIATED',
        filter: {
          provider_id: provider.id,
          serviceline:
            this.state.serviceline === 'all'
              ? undefined
              : this.state.serviceline,
          before: this.state.before,
          taxonomy_code:
            this.state.specialty.length < 1 ? undefined : this.state.specialty,
          client_id: clientId,
          patient_type: patientType,
        },
        opts: {
          limit: 32,
          offset: 0,
        },
      })
    );
  }

  // Options for the Specialty dropdown: the distinct specialties of the
  // providers that actually share patients with this provider.
  //
  // ClaimsNetwork (the card above this one on the same page) loads the same
  // CLAIMS_NETWORK_SPECIALTIES analytic, so this dispatch carries a store_id to
  // land in its own slot in state.analytics -- without it the two cards would
  // overwrite each other's results.
  //
  // Deliberately NOT scoped by this.state.serviceline: here `serviceline` means
  // "show the children of this service line", while CLAIMS_NETWORK_SPECIALTIES
  // matches serviceline_id exactly. Scoping by it would return the wrong list.
  // Keeping the list unscoped also means a selection survives a drill-down.
  fetchSpecialtiesData() {
    const { dispatch, provider, patientType } = this.props;

    dispatch(
      loadAnalytics({
        type: 'CLAIMS_NETWORK_SPECIALTIES',
        store_id: SPECIALTIES_STORE_ID,
        filter: {
          provider_id: provider.id,
          before: this.state.before,
          patient_type: patientType,
          // This card's data query counts small-cell-suppressed rows as
          // COALESCE(shared_patients, 5), so the option list has to include
          // specialties that appear ONLY in suppressed rows. Otherwise
          // selecting every specialty would total less than "All".
          include_suppressed: true,
        },
      })
    );
  }

  render() {
    const { classes, network, provider, servicelines, patientType } = this.props;
    const { serviceline, specialty } = this.state;
    const isLoaded = !(!network || !StoreUtil.isLoaded(network) || !servicelines);

    let specialties = [];
    if (StoreUtil.isLoaded(this.props.specialties)) {
      specialties = StoreUtil.getData(this.props.specialties).sort((a, b) =>
        SpecialtyUtil.getNameFromTaxonomy(a).localeCompare(
          SpecialtyUtil.getNameFromTaxonomy(b)
        )
      );
    }
    // Some taxonomy names are ambiguous (several codes share a display name).
    // Count them so the menu can disambiguate with the code, as ClaimsNetwork does.
    const specialtyNameCounts = {};
    specialties.forEach((s) => {
      const name = SpecialtyUtil.getNameFromTaxonomy(s);
      if (name.trim().length === 0) return;
      specialtyNameCounts[name] = (specialtyNameCounts[name] || 0) + 1;
    });
    const selectedTaxonomies =
      specialty.length > 0
        ? specialty
            .map((code) =>
              this.props.specialties?.data?.find((s) => s.code === code)
            )
            .filter(Boolean)
            .map((s) => ({
              name: SpecialtyUtil.getNameFromTaxonomy(s),
              code: s.code,
            }))
        : null;

    const categories = [];
    const series = [
      { name: 'Affiliated', data: [] },
      { name: 'Non-Affiliated', data: [] },
    ];

    const showSpecificServicelineIds =
      serviceline === 'all' ? [] : [serviceline];
    const visibleRows = [];
    network?.data?.rows?.forEach((row) => {
      if (
        (serviceline === 'all' && row.parent_id === null) ||
        row.parent_id === serviceline
      ) {
        visibleRows.push(row);
        series[0].data.push(row.sum_shared_patients_affiliated);
        series[1].data.push(row.sum_shared_patients_non_affiliated);

        if (row.serviceline_id && row.sum_shared_patients) {
          showSpecificServicelineIds.push(row.serviceline_id);
        }
        categories.push({
          label: row.abbreviation ?? row.name,
          total: row.sum_shared_patients,
          affiliatedPercentage: row.sum_shared_patients
            ? Math.round(
                (row.sum_shared_patients_affiliated / row.sum_shared_patients) *
                  100
              )
            : 0,
          isParent: row.parent_id === null,
          servicelineId: row.serviceline_id,
          affiliatedCount: row.sum_shared_patients_affiliated || 0,
          nonAffiliatedCount: row.sum_shared_patients_non_affiliated || 0,
        });
      }
    });

    const hasChildren =
      serviceline === 'all'
        ? true
        : ServiceLineUtil.hasChildren(servicelines, serviceline);

    const pdfChartData = visibleRows.map((row) => ({
      name: row.name ?? row.abbreviation,
      shared_patients: row.sum_shared_patients,
      shared_patients_affiliated: row.sum_shared_patients_affiliated,
    }));
    const selectedServiceLine =
      serviceline !== 'all'
        ? ServiceLineUtil.findServiceLine(servicelines, serviceline)
        : null;
    const beforeOrAfter = this.state.before ? 'Before' : 'After';

    // Affiliated / Non-Affiliated summary shown above the chart.
    // Percentages only, by design: the underlying shared-patient counts are
    // deliberately NOT surfaced here. They are projected volumes that are not
    // unique across service lines, so any total we could show would overstate
    // the real patient count and invite more questions than it answers.
    // At the top level ('all') the percentage is a volume-weighted rollup
    // across service lines, which is an approximation, so it renders with a
    // leading '~'. When drilled into a single service line it reuses that
    // line's EXACT percentage from the top-level view (cached below), so it
    // renders without the '~'.
    // The cached top-level percentages are only comparable within the same
    // filter context -- the specialty selection MUST be part of the key or a
    // drill-down would reuse exact figures computed from a different data set.
    // Sorted so the key depends on WHICH specialties are selected, not the
    // order they were clicked in.
    const summaryContextKey = `${provider?.id}:${this.state.before}:${patientType}:${[...specialty].sort().join(',')}`;
    let summaryAffiliatedPct = null;
    let summaryIsApprox = true;
    const sumVisibleRows = (rows) => {
      let total = 0;
      let affiliated = 0;
      rows.forEach((row) => {
        total += row.sum_shared_patients || 0;
        affiliated += row.sum_shared_patients_affiliated || 0;
      });
      return {
        total,
        affiliated,
        pct: total > 0 ? Math.round((affiliated / total) * 100) : null,
      };
    };
    if (serviceline === 'all') {
      const agg = sumVisibleRows(visibleRows);
      summaryAffiliatedPct = agg.pct;
      summaryIsApprox = true;
      // Cache each top-level service line's exact counts for drilldown reuse.
      // Gated on isLoaded: while a refetch is in flight the store still holds
      // the PREVIOUS filter's rows (setLoading keeps existing data), so writing
      // here would stamp the new context key onto stale totals and the
      // drilldown would then present them as exact (no leading '~').
      if (isLoaded) {
        const byId = {};
        visibleRows.forEach((row) => {
          if (row.serviceline_id != null) {
            byId[row.serviceline_id] = {
              affiliated: row.sum_shared_patients_affiliated || 0,
              total: row.sum_shared_patients || 0,
            };
          }
        });
        this.topLevelAffiliatedByServiceline = { key: summaryContextKey, byId };
      }
    } else {
      const cache = this.topLevelAffiliatedByServiceline;
      const cached =
        cache && cache.key === summaryContextKey
          ? cache.byId[serviceline]
          : null;
      if (cached && cached.total > 0) {
        // Exact figures carried down from the top-level breakdown (no '~').
        summaryAffiliatedPct = Math.round(
          (cached.affiliated / cached.total) * 100
        );
        summaryIsApprox = false;
      } else {
        // Fallback (e.g. deep-linked before the top level was rendered):
        // approximate from the visible sub-service-line rows.
        const agg = sumVisibleRows(visibleRows);
        summaryAffiliatedPct = agg.pct;
        summaryIsApprox = true;
      }
    }

    return (
      <>
      <Card className={classes.card} ref={this.cardElement}>
        <CardContent>
          <TitleTooltip
            title='Shared Patient Provider Affiliation by Service Line'
            onExport={this.exportChart}
            onPdfExport={() => this.setState({ showPdfExport: true })}
            csvExportOptions={{
              exportType: 'CLAIMS_SHARED_AFFILIATION',
              totalCount: this.props.network?.data?.rows?.length,
              fileName: `doctivity_SHARED_AFFILIATION_${DateUtil.formatDateForUser(new Date())}.csv`,
            }}
          />
          <div className={classes.dropDownContainer}>
            <FormControl
              variant='outlined'
              className={classes.filterDropDown}
              key='direction'
            >
              <InputLabel className={classes.filterLabel}>
                Claims Date
              </InputLabel>
              <Select
                key='select'
                value={this.state.before}
                onChange={this.onDirectionChange}
                label='Claims Date'
              >
                <MenuItem value={false} key='after'>
                  {`After ${provider.last_name}, ${provider.first_name}`}
                </MenuItem>
                <MenuItem value key='before'>
                  {`Before ${provider.last_name}, ${provider.first_name}`}
                </MenuItem>
              </Select>
            </FormControl>
            <div className={classes.dropDown}>
              <ServiceLineSelect
                value={serviceline}
                onChange={this.onServicelineChange}
                hideChildlessTopLevels
                showSpecificServicelineIds={
                  serviceline === 'all' && showSpecificServicelineIds
                }
                showAll
              />
            </div>
            <FormControl
              variant='outlined'
              className={classes.specialtyDropDown}
              key='specialty'
            >
              <InputLabel shrink className={classes.filterLabel}>
                Specialty
              </InputLabel>
              <Select
                key='select'
                value={specialty}
                onChange={this.onSpecialtyChange}
                multiple
                label='Specialty'
                notched
                displayEmpty
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>All</em>;
                  }
                  return selected
                    .map((code) => {
                      const found = this.props.specialties?.data?.find(
                        (sp) => sp.code === code
                      );
                      return found
                        ? SpecialtyUtil.getNameFromTaxonomy(found)
                        : code;
                    })
                    .join(', ');
                }}
                MenuProps={{
                  PaperProps: {
                    className: classes.select,
                  },
                }}
              >
                <ListSubheader
                  // MUI clones every Select child with an item click handler,
                  // ListSubheader included. Without this, clicking the header
                  // pushes `undefined` into the selection and the chart empties.
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    borderBottom: '1px solid black',
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#000',
                  }}
                >
                  {specialty.length} selected
                </ListSubheader>
                <MenuItem autoFocus value='all' key='all'>
                  <em>All</em>
                </MenuItem>
                {specialties.map((s) => (
                  <MenuItem value={s.code} key={s.code}>
                    {SpecialtyUtil.getNameFromTaxonomy(s)}&nbsp;
                    {specialtyNameCounts[SpecialtyUtil.getNameFromTaxonomy(s)] >
                    1
                      ? `(${s.code})`
                      : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className={classes.dropDown}>
              <PatientTypeSelect noCapturedClaims />
            </div>
          </div>
          {serviceline !== 'all' && (
            <SmallIconTextButton
              icon='chevron_left'
              text='Top Level Service Lines'
              onClick={() => this.onServicelineChange('all')}
            />
          )}
          {isLoaded && summaryAffiliatedPct !== null && (
            <div className={classes.affiliatedContainer}>
              <div className={classes.affiliatedHeader}>
                <span className={classes.affiliated}>
                  <Icon className={classes.affiliatedIcon}>hub</Icon>
                  <span className={classes.affiliatedPercentage}>
                    {summaryIsApprox ? '~' : ''}
                    {summaryAffiliatedPct}%
                  </span>
                  <span>Affiliated</span>
                </span>
                <div className={classes.affiliatedSpacer} />
                <span className={classes.nonAffiliated}>
                  <span className={classes.affiliatedPercentage}>
                    {summaryIsApprox ? '~' : ''}
                    {100 - summaryAffiliatedPct}%
                  </span>
                  <span>Non-Affiliated</span>
                </span>
              </div>
              <Tooltip
                placement='bottom-start'
                title={
                  <Typography className={classes.affiliatedInfoTip}>
                    {summaryIsApprox
                      ? 'Projected patients are not unique across service lines, so this combined percentage is an approximation.'
                      : 'The exact affiliated share for this service line, carried from the top-level breakdown.'}
                  </Typography>
                }
              >
                <Icon className={classes.affiliatedInfoTipIcon}>
                  info_outlined
                </Icon>
              </Tooltip>
            </div>
          )}
          {!isLoaded && <LoadingView />}
          {isLoaded && network.data.rows.length === 0 && hasChildren && (
            <Typography className={classes.noData}>
              No shared patients.
            </Typography>
          )}
          {isLoaded && network.data.rows.length === 0 && !hasChildren && (
            <Typography className={classes.noData}>
              No Sub-Service Lines available for this Service Line
            </Typography>
          )}
          {isLoaded && network.data.rows.length > 0 && (
            <Chart
              type='bar'
              height={Math.max(50 + network.data.rows.length * 26, 180)}
              options={{
                chart: {
                  id: 'ClaimsByServicelineAffiliated',
                  toolbar: {
                    show: false,
                  },
                  events: {
                    click: this.onChartClick,
                  },
                  animations: {
                    enabled: false,
                  },
                  stacked: true,
                },
                yaxis: {
                  labels: {
                    formatter: (obj) => obj.label,
                    minWidth: 100,
                  },
                },
                xaxis: {
                  categories,
                  labels: {
                    show: false,
                    hideOverlappingLabels: true,
                    rotate: -45,
                    formatter: StringUtil.numberForChart,
                  },
                  tickPlacement: 'on',
                },
                tooltip: {
                  enabled: true,
                  shared: true,
                  intersect: false,
                  x: {
                    formatter: (x, opt) => {
                      const cat =
                        opt?.w?.config?.xaxis?.categories?.[opt.dataPointIndex];
                      return cat?.total != null
                        ? `${cat.label} — ${cat.total.toLocaleString()} shared`
                        : `${x.label}`;
                    },
                  },
                  y: {
                    formatter: (y, opt) => {
                      const cat =
                        opt.w.config.xaxis.categories[opt.dataPointIndex];
                      if (opt.seriesIndex === 0) {
                        return `${cat.affiliatedPercentage}% (${(
                          cat.affiliatedCount ?? 0
                        ).toLocaleString()})`;
                      }
                      return `${100 - cat.affiliatedPercentage}% (${(
                        cat.nonAffiliatedCount ?? 0
                      ).toLocaleString()})`;
                    },
                  },
                },
                colors: [
                  ColorUtil.AffiliatedColor,
                  ColorUtil.NonAffiliatedColor,
                ],
                dataLabels: {
                  enabled: true,
                  hideOverflowingLabels: true,
                  dropShadow: {
                    enabled: true,
                    left: 0,
                    top: 0,
                    opacity: 0.4,
                  },
                  formatter: (val, opt) => {
                    if (!val) {
                      return '';
                    }

                    if (opt.seriesIndex === 0) {
                      return `${
                        opt.w.config.xaxis.categories[opt.dataPointIndex]
                          .affiliatedPercentage
                      }%`;
                    } else {
                      return `${
                        100 -
                        opt.w.config.xaxis.categories[opt.dataPointIndex]
                          .affiliatedPercentage
                      }%`;
                    }
                  },
                  offsetY: 0,
                },
                plotOptions: {
                  bar: {
                    horizontal: true,
                  },
                },
              }}
              series={series}
            />
          )}
        </CardContent>
      </Card>
      <PDFExportDialog
        open={this.state.showPdfExport}
        onClose={() => this.setState({ showPdfExport: false })}
        title={`Shared Patient Provider Affiliation by Service Line (${beforeOrAfter}) for ${provider.last_name}, ${provider.first_name}`}
        reportType='shared_affiliation'
        chartData={pdfChartData}
        totalCount={pdfChartData.length}
        defaultMaxRows={pdfChartData.length}
        defaultOptions={{ claimsType: 'servicelines' }}
        filters={{
          provider,
          patientType,
          serviceLine: selectedServiceLine,
          taxonomies: selectedTaxonomies,
          claimsDate: this.state.before,
          before: this.state.before,
        }}
      />
      </>
    );
  }

  exportChart = ({ limit, offset = 0, fileName } = {}) => {
    // 'Shared Provider Specialty' records the Specialty filter that produced
    // these numbers. The NPI/name columns are the subject provider, not the
    // shared providers, so the filter has nowhere else to show up in the CSV.
    const columns = ['Provider NPI', 'First Name', 'Last Name', 'Before/After', 'Affiliation Status', 'Shared Provider Specialty', 'Service Line', 'Patients', '% Affiliated', '% Non Affiliated'];
    const data = [columns];
    const { network, provider, servicelines, patientType, specialties } = this.props;
    const beforeOrAfter = this.state.before ? 'Before' : 'After';
    // CsvUtil.matrixToString joins on ',' with no quoting, so join the selected
    // specialty names with ';' -- a ',' would split them into extra columns.
    const specialtyLabel =
      this.state.specialty.length > 0
        ? this.state.specialty
            .map((code) => {
              const found = specialties?.data?.find((sp) => sp.code === code);
              return found ? SpecialtyUtil.getNameFromTaxonomy(found) : code;
            })
            .join('; ')
        : 'All';
    const sourceRows = typeof limit === 'number'
      ? (network.data.rows || []).slice(offset, offset + limit)
      : network.data.rows;
    sourceRows.forEach((row) => {
      const sharedPatients = parseInt(row.sum_shared_patients, 10) || 0;
      data.push([
        provider.npi,
        provider.first_name,
        provider.last_name,
        beforeOrAfter,
        provider.clients?.length > 0 ? 'Affiliated' : 'Non-affiliated',
        specialtyLabel,
        row.abbreviation ?? row.name,
        // Mirrors the PDF's PATIENTS column (chartRenderer drawRow): total
        // shared patients for the service line, with the same '<11'
        // suppression marker, so the CSV and PDF exports agree. Emitted as a
        // raw integer, never toLocaleString() — CsvUtil.matrixToString joins
        // on ',' with no quoting, so a thousands separator would split this
        // into two columns.
        sharedPatients > 0 ? sharedPatients : '<11',
        ((row.sum_shared_patients_affiliated / row.sum_shared_patients )*100).toFixed(2),
        ((row.sum_shared_patients_non_affiliated / row.sum_shared_patients )*100).toFixed(2),

      ])
    });
    const serviceline = this.state.serviceline === 'all' ? 'ALL' : servicelines.find((l) => l.id === this.state.serviceline);
    const pediatric = patientType === 'PEDIATRIC_PATIENTS' ? '_PEDIATRIC' : '';
    const specialtySuffix =
      this.state.specialty.length === 1
        ? `_${this.state.specialty[0]}`
        : this.state.specialty.length > 1
          ? `_${this.state.specialty.length}SPECIALTIES`
          : '';
    const date = DateUtil.formatDateForUser(new Date());
    fileDownload(CsvUtil.matrixToString(data), fileName || `doctivity_SHARED_${beforeOrAfter.toLocaleUpperCase()}_${((serviceline?.abbreviation??serviceline?.name)??'ALL').toLocaleUpperCase()}${specialtySuffix}${pediatric}_${date}.csv`);
  }

  onChartClick = (event, chartContext, config) => {
    const { isMobile } = this.props;
    if (!isMobile && config.dataPointIndex > -1) {
      const selectedServiceline =
        config.config.xaxis.categories[config.dataPointIndex];
      if (selectedServiceline.isParent) {
        this.setState(
          {
            serviceline: selectedServiceline.servicelineId,
          },
          this.fetchData
        );
      }
    }
  };

  onServicelineChange = (id) => {
    this.setState(
      {
        serviceline: id,
      },
      this.fetchData
    );
  };

  // Mirrors ClaimsNetwork.onSpecialtyChange: picking the "All" item clears the
  // selection, and 'all' is never allowed to persist among the selected codes.
  onSpecialtyChange = (event, value) => {
    if (value?.key === '.$all') {
      this.setState({ specialty: [] }, this.fetchData);
      return;
    }
    this.setState(
      {
        // Drop the 'all' sentinel, and any undefined a stray click on a
        // non-value child could inject.
        specialty: event.target.value.filter((s) => s && s !== 'all'),
      },
      this.fetchData
    );
  };

  onDirectionChange = (event) => {
    // Before/After swaps which side of the relationship the shared providers
    // sit on, so the available specialties change too. Reset to "All" and
    // reload the option list.
    this.setState(
      {
        before: event.target.value,
        specialty: [],
      },
      () => {
        this.fetchData();
        this.fetchSpecialtiesData();
      }
    );
  };
}

ClaimsNetworkAffiliated.propTypes = {
  classes: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
  isMobile: PropTypes.bool.isRequired,
  provider: PropTypes.object,
  network: PropTypes.object,
  servicelines: PropTypes.array,
  specialties: PropTypes.object,
  patientType: PropTypes.oneOf(PatientTypes),
};

function mapStateToProps(state) {
  return {
    clientId: state.app.selectedClient,
    network: StoreUtil.get(state.analytics, 'CLAIMS_NETWORK_AFFILIATED'),
    specialties: StoreUtil.get(state.analytics, SPECIALTIES_STORE_ID),
    servicelines: state.claims && state.claims.servicelines_grouped,
    patientType:
      state.app.patientType === PatientType.CAPTURED_CLAIMS
        ? PatientType.ALL_PATIENTS
        : state.app.patientType,
  };
}

const styled = withIsMobile(withStyles(styles)(ClaimsNetworkAffiliated));
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as ClaimsNetworkAffiliated };
